import React, { useState, useEffect } from 'react'
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, ClockIcon, CalendarIcon, CpuChipIcon, PlayCircleIcon, PauseCircleIcon } from '@heroicons/react/24/outline'
import { syncOrders, getScheduledStatus, syncOrdersByDate, enableScheduledTask, disableScheduledTask } from '../api/homecenterService'
import { SyncOrdersResponse, ScheduledStatusResponse, TimeUntilNext } from '../types/homecenter'
import SyncResultsModal from '../components/modals/SyncResultsModal'
import toast from 'react-hot-toast'

// Componente para la cuenta regresiva
const CountdownTimer: React.FC<{ timeUntilNext: TimeUntilNext | undefined }> = ({ timeUntilNext }) => {
  const [timeLeft, setTimeLeft] = useState<TimeUntilNext | undefined>(timeUntilNext)
  const [isExpired, setIsExpired] = useState(false)

  useEffect(() => {
    if (!timeUntilNext || !timeUntilNext.totalMilliseconds) return

    // Inicializar el estado con los datos recibidos
    setTimeLeft(timeUntilNext)
    setIsExpired(false)

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (!prev || prev.totalMilliseconds <= 1000) {
          setIsExpired(true)
          clearInterval(interval)
          // Cuando llegue a 0, recargar la página después de 2 segundos
          setTimeout(() => {
            window.location.reload()
          }, 2000)
          return prev
        }
        
        const newTotalMs = prev.totalMilliseconds - 1000
        const hours = Math.floor(newTotalMs / (1000 * 60 * 60))
        const minutes = Math.floor((newTotalMs % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((newTotalMs % (1000 * 60)) / 1000)
        
        return {
          hours,
          minutes,
          seconds,
          totalMilliseconds: newTotalMs,
          humanReadable: `${hours}h ${minutes}m ${seconds}s`
        }
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [timeUntilNext])

  if (!timeLeft || isExpired) {
    return (
      <div className="text-sm font-semibold text-red-600 leading-tight">
        <div className="text-lg font-bold">
          {isExpired ? 'Ejecutando...' : 'No disponible'}
        </div>
      </div>
    )
  }

  return (
    <div className="text-sm font-semibold text-blue-600 leading-tight">
      <div className="text-lg font-bold">
        {timeLeft.humanReadable}
      </div>
    </div>
  )
}

const HomeCenterSync: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [syncResult, setSyncResult] = useState<SyncOrdersResponse | null>(null)
  const [tipoOrden, setTipoOrden] = useState<number>(1)
  const [scheduledStatus, setScheduledStatus] = useState<ScheduledStatusResponse | null>(null)
  const [isLoadingStatus, setIsLoadingStatus] = useState(false)
  const [syncType, setSyncType] = useState<'all' | 'dateRange'>('all')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [showResultsModal, setShowResultsModal] = useState(false)
  const [syncResults, setSyncResults] = useState<SyncOrdersResponse | null>(null)

  // Función para generar fechas por rango seleccionado
  const generateDateRangeDates = (start: string, end: string) => {
    const dates = []
    const startDate = new Date(start)
    const endDate = new Date(end)
    
    // Calcular la diferencia en días
    const diffTime = endDate.getTime() - startDate.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    for (let i = 0; i <= diffDays; i++) {
      const currentDate = new Date(startDate)
      currentDate.setDate(startDate.getDate() + i)
      
      // Formatear fecha como YYYY-MM-DD
      const fecha = currentDate.toISOString().split('T')[0]
      
      dates.push({
        fecha: fecha,
        dayLabel: currentDate.toLocaleDateString('es-CO', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })
      })
    }
    
    return dates
  }

  const handleSync = async () => {
    setIsLoading(true)
    setSyncResult(null)
    
    try {
      if (syncType === 'all') {
        // Sincronización de todos los pedidos
        const result = await syncOrders({ tipoOrden })
        setSyncResult(result)
        setSyncResults(result)
        setShowResultsModal(true)
      } else {
        // Validar que se hayan seleccionado las fechas
        if (!startDate || !endDate) {
          setSyncResults({
            success: false,
            message: 'Por favor selecciona las fechas de inicio y fin',
            results: { total: 0, created: 0, skipped: 0, errors: 0 }
          })
          setShowResultsModal(true)
          setIsLoading(false)
          return
        }
        
        // Validar que la fecha de inicio sea anterior a la fecha de fin
        if (new Date(startDate) > new Date(endDate)) {
          setSyncResults({
            success: false,
            message: 'La fecha de inicio debe ser anterior a la fecha de fin',
            results: { total: 0, created: 0, skipped: 0, errors: 0 }
          })
          setShowResultsModal(true)
          setIsLoading(false)
          return
        }
        
        // Validar que el rango no exceda 30 días
        const diffTime = new Date(endDate).getTime() - new Date(startDate).getTime()
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        if (diffDays > 30) {
          setSyncResults({
            success: false,
            message: 'El rango de fechas no puede exceder los 30 días',
            results: { total: 0, created: 0, skipped: 0, errors: 0 }
          })
          setShowResultsModal(true)
          setIsLoading(false)
          return
        }
        
        // Sincronización por rango de fechas
        const dates = generateDateRangeDates(startDate, endDate)
        let totalResults = {
          total: 0,
          created: 0,
          skipped: 0,
          errors: 0
        }
        
        let successCount = 0
        let errorCount = 0
        
        for (let i = 0; i < dates.length; i++) {
          const dateRange = dates[i]
          try {
            const result = await syncOrdersByDate({
              tipoOrden,
              fecha: dateRange.fecha
            })
            
            if (result.success) {
              totalResults.total += result.results.total
              totalResults.created += result.results.created
              totalResults.skipped += result.results.skipped
              totalResults.errors += result.results.errors
              successCount++
            } else {
              errorCount++
            }
          } catch (error: any) {
            console.error(`Error sincronizando ${dateRange.dayLabel}:`, error)
            errorCount++
          }
        }
        
        // Mostrar resultado consolidado
        const consolidatedResult: SyncOrdersResponse = {
          success: errorCount === 0,
          message: `Sincronización por rango de fechas completada. ${successCount} días exitosos, ${errorCount} con errores.`,
          results: totalResults
        }
        
        setSyncResult(consolidatedResult)
        setSyncResults(consolidatedResult)
        setShowResultsModal(true)
      }
    } catch (error: any) {
      console.error('Error en sincronización:', error)
      const errorResult: SyncOrdersResponse = {
        success: false,
        message: error.response?.data?.message || 'Error al sincronizar pedidos',
        results: { total: 0, created: 0, skipped: 0, errors: 0 }
      }
      setSyncResults(errorResult)
      setShowResultsModal(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleScheduledTask = async () => {
    if (!scheduledStatus?.scheduledTasks?.homeCenterSync) return
    
    setIsLoading(true)
    
    try {
      const isCurrentlyEnabled = scheduledStatus.scheduledTasks.homeCenterSync.enabled
      
      if (isCurrentlyEnabled) {
        await disableScheduledTask('homeCenterSync')
        toast.success('Sincronización programada desactivada correctamente', { position: 'top-center' })
      } else {
        await enableScheduledTask('homeCenterSync')
        toast.success('Sincronización programada activada correctamente', { position: 'top-center' })
      }
      
      // Actualizar el estado local
      setScheduledStatus(prev => ({
        ...prev!,
        scheduledTasks: {
          ...prev!.scheduledTasks!,
          homeCenterSync: {
            ...prev!.scheduledTasks!.homeCenterSync!,
            enabled: !isCurrentlyEnabled
          }
        }
      }))
      
    } catch (error: any) {
      console.error('Error al cambiar estado de tarea programada:', error)
      const isCurrentlyEnabled = scheduledStatus.scheduledTasks.homeCenterSync.enabled
      toast.error(
        isCurrentlyEnabled 
          ? 'Error al desactivar la sincronización programada'
          : 'Error al activar la sincronización programada',
        { position: 'top-center' }
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleCheckStatus = async () => {
    setIsLoadingStatus(true)
    
    try {
      const result = await getScheduledStatus()
      setScheduledStatus(result)
    } catch (error: any) {
      console.error('Error obteniendo estado:', error)
    } finally {
      setIsLoadingStatus(false)
    }
  }

  // Cargar estado inicial al montar el componente
  useEffect(() => {
    handleCheckStatus()
  }, [])

  const getStatusIcon = () => {
    if (isLoading) {
      return <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
    }
    if (syncResult?.success) {
      return <CheckCircleIcon className="h-8 w-8 text-green-500" />
    }
    return <InformationCircleIcon className="h-8 w-8 text-gray-400" />
  }

  const getStatusColor = () => {
    if (isLoading) return 'border-blue-200 bg-blue-50'
    if (syncResult?.success) return 'border-green-200 bg-green-50'
    return 'border-gray-200 bg-gray-50'
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h3 className="  mb-2">
          Sincronizar Pedidos Home Center
        </h3>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            {getStatusIcon()}
            <div>
              <h2 className="text-xl font-semibold text-custom-blue">
                Estado de Sincronización
              </h2>
              <p className="text-sm text-gray-500">
                {isLoading 
                  ? 'Sincronizando pedidos...' 
                  : syncResult 
                    ? 'Última sincronización completada' 
                    : 'Listo para sincronizar'
                }
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 items-end mb-6">
          <div className="flex-1">
            <label htmlFor="syncType" className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Sincronización
            </label>
            <select
              id="syncType"
              value={syncType}
              onChange={(e) => setSyncType(e.target.value as 'all' | 'dateRange')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            >
              <option value="all">Todos los pedidos</option>
              <option value="dateRange">Sincronizar por fecha</option>
            </select>
          </div>
          
          <div className="flex-1">
            <label htmlFor="tipoOrden" className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Orden
            </label>
            <select
              id="tipoOrden"
              value={tipoOrden}
              onChange={(e) => setTipoOrden(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            >
              <option value={1}>MARKETPLACE</option>
               <option value={4}>EMPRESAS</option>
               <option value={8}>FBS PROVEEDOR</option>
            </select>
          </div>
          
          {syncType === 'dateRange' && (
            <>
              <div className="flex-1">
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Inicio
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoading}
                  required
                />
              </div>
              
              <div className="flex-1">
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Fin
                </label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoading}
                  required
                />
              </div>
            </>
          )}
          
          <div className="flex gap-2">
            <button
              onClick={handleSync}
              disabled={isLoading}
              className={`px-4 py-3 rounded-lg font-medium transition-colors duration-200 ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  <span>
                    {syncType === 'dateRange' ? 'Sincronizando fechas...' : 'Sincronizando...'}
                  </span>
                </div>
              ) : (
                syncType === 'dateRange' ? 'Sincronizar Fechas' : 'Sincronizar'
              )}
            </button>
            
          </div>
        </div>

        {/* Estado de Sincronización Programada */}
        {scheduledStatus && (
          <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <ClockIcon className="h-6 w-6 text-gray-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Estado de Sincronización Programada
                  </h3>
                  <p className="text-sm text-gray-600">
                    {scheduledStatus.scheduledTasks?.homeCenterSync?.enabled ? 'Activa' : 'Inactiva'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCheckStatus}
                  disabled={isLoadingStatus}
                  className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50">
                  {isLoadingStatus ? 'Actualizando...' : 'Actualizar'}
                </button>
                
                <button
                  onClick={handleToggleScheduledTask}
                  disabled={isLoading || !scheduledStatus?.scheduledTasks?.homeCenterSync}
                  className={`px-3 py-2 text-sm rounded-md transition-colors duration-200 ${
                    isLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : scheduledStatus?.scheduledTasks?.homeCenterSync?.enabled
                        ? 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                        : 'bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
                  }`}
                  title={scheduledStatus?.scheduledTasks?.homeCenterSync?.enabled 
                    ? 'Desactivar Sincronización Programada' 
                    : 'Activar Sincronización Programada'
                  }
                >
                  {isLoading ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  ) : scheduledStatus?.scheduledTasks?.homeCenterSync?.enabled ? (
                    <PauseCircleIcon className="h-4 w-4" />
                  ) : (
                    <PlayCircleIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-2 mb-2">
                  <CpuChipIcon className="h-5 w-5 text-gray-500" />
                  <div className="text-sm font-medium text-gray-600">Estado</div>
                </div>
                <div className={`text-lg font-semibold flex items-center space-x-2 ${
                  scheduledStatus.scheduledTasks?.homeCenterSync?.enabled ? 'text-green-600' : 'text-gray-600'
                }`}>
                  <div className={`w-3 h-3 rounded-full ${
                    scheduledStatus.scheduledTasks?.homeCenterSync?.enabled ? 'bg-green-500' : 'bg-gray-400'
                  }`}></div>
                  <span>{scheduledStatus.scheduledTasks?.homeCenterSync?.enabled ? 'Activa' : 'Inactiva'}</span>
                </div>
              </div>
              
              <div className="p-4 bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-2 mb-2">
                  <ClockIcon className="h-5 w-5 text-gray-500" />
                  <div className="text-sm font-medium text-gray-600">Frecuencia</div>
                </div>
                <div className="text-lg font-semibold text-blue-600">
                  {scheduledStatus.scheduledTasks?.homeCenterSync?.frequency || 'No configurada'}
                </div>
              </div>
              
              <div className="p-4 bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-2 mb-2">
                  <CalendarIcon className="h-5 w-5 text-gray-500" />
                  <div className="text-sm font-medium text-gray-600">Próxima Ejecución</div>
                </div>
                <div className="text-sm font-semibold text-purple-600 leading-tight">
                  {scheduledStatus.scheduledTasks?.homeCenterSync?.nextExecution || 'No programada'}
                </div>
              </div>

              {/* Nueva tarjeta para la cuenta regresiva */}
              <div className="p-4 bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-2 mb-2">
                  <ClockIcon className="h-5 w-5 text-gray-500" />
                  <div className="text-sm font-medium text-gray-600">Tiempo Restante</div>
                </div>
                {scheduledStatus.scheduledTasks?.homeCenterSync?.timeUntilNext ? (
                  <CountdownTimer timeUntilNext={scheduledStatus.scheduledTasks.homeCenterSync.timeUntilNext} />
                ) : (
                  <div className="text-sm font-semibold text-gray-500">
                    No disponible
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {syncResult && (
          <div className={`mt-6 p-4 rounded-lg border ${getStatusColor()}`}>
            <div className="flex items-center space-x-2 mb-4">
              {syncResult.success ? (
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
              ) : (
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
              )}
              <h3 className="text-lg font-semibold text-gray-900">
                {syncResult.success ? 'Sincronización Exitosa' : 'Error en Sincronización'}
              </h3>
            </div>
            
            <p className="text-gray-700 mb-4">{syncResult.message}</p>
            
            {syncResult.results && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-blue-600">
                    {syncResult.results.total}
                  </div>
                  <div className="text-sm text-gray-600">Total</div>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-green-600">
                    {syncResult.results.created}
                  </div>
                  <div className="text-sm text-gray-600">Creados</div>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-yellow-600">
                    {syncResult.results.skipped}
                  </div>
                  <div className="text-sm text-gray-600">Omitidos</div>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-red-600">
                    {syncResult.results.errors}
                  </div>
                  <div className="text-sm text-gray-600">Errores</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <InformationCircleIcon className="h-5 w-5 text-blue-500 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-blue-800 mb-1">
              Información sobre la Sincronización
            </h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• <strong>Todos los pedidos:</strong> Sincroniza todos los pedidos disponibles en Home Center</li>
              <li>• <strong>Sincronizar por fecha:</strong> Sincroniza pedidos día por día en el rango de fechas seleccionado</li>
              <li>• Los datos se transforman al formato del sistema interno</li>
              <li>• Se guardan en la base de datos del hosting</li>
              <li>• Se envían automáticamente a Ofima</li>
              <li>• El proceso puede tomar varios minutos dependiendo del volumen</li>
              <li>• La sincronización por fechas hace una consulta por cada día en el rango seleccionado</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Modal de resultados de sincronización */}
      <SyncResultsModal
        isOpen={showResultsModal}
        onClose={() => setShowResultsModal(false)}
        results={syncResults}
        isLoading={isLoading}
        syncType={syncType}
        dateRange={syncType === 'dateRange' ? { startDate, endDate } : undefined}
      />
    </div>
  )
}

export default HomeCenterSync